import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FieldSelect } from "@/components/ui/field-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LifeBuoy, Plus, Loader2, Paperclip, Send, Search } from "lucide-react";

const CATS = { technical: "Problème technique", billing: "Abonnement & facturation", help: "Besoin d'aide", improvement: "Demande d'amélioration", other: "Autre" };
const STATUS = { nouveau: ["Nouveau", "bg-blue-50 text-blue-700"], en_cours: ["En cours", "bg-amber-50 text-amber-700"], en_attente_client: ["En attente client", "bg-purple-50 text-purple-700"], resolu: ["Résolu", "bg-emerald-50 text-emerald-700"] };
const CAT_OPTS = Object.entries(CATS).map(([value, label]) => ({ value, label }));

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return `il y a ${Math.floor(s / 86400)} j`;
}
async function uploadFile(orgId, file) {
  const fd = new FormData(); fd.append("file", file);
  const { data } = await api.post("/api/support/attachments", fd, { params: { organization_id: orgId } });
  return data.file_id;
}

export default function Support() {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState("all"); const [category, setCategory] = useState("all"); const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(false); const [openId, setOpenId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["support-tickets", orgId, status, category, q],
    queryFn: async () => (await api.get("/api/support/tickets", { params: { organization_id: orgId, status: status === "all" ? undefined : status, category: category === "all" ? undefined : category, q: q || undefined } })).data,
    enabled: !!orgId, retry: false,
  });
  const tickets = data?.items || [];

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-8" data-testid="support-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><LifeBuoy className="h-5 w-5" /></div>
          <div><h1 className="text-2xl font-bold tracking-tight text-slate-900">Support</h1>
            <p className="text-sm text-slate-500">Vos demandes d'assistance ActiPilote</p></div>
        </div>
        <button onClick={() => setNewOpen(true)} data-testid="new-ticket-button" className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
          <Plus className="h-4 w-4" /> Nouvelle demande
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (numéro, objet)" data-testid="support-search"
            className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none" />
        </div>
        <FieldSelect testId="support-filter-status" className="w-44" value={status} onChange={setStatus} placeholder="Tous les statuts"
          options={[{ value: "all", label: "Tous les statuts" }, ...Object.entries(STATUS).map(([v, [l]]) => ({ value: v, label: l }))]} />
        <FieldSelect testId="support-filter-category" className="w-52" value={category} onChange={setCategory} placeholder="Toutes les catégories"
          options={[{ value: "all", label: "Toutes les catégories" }, ...CAT_OPTS]} />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : tickets.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="ticket-empty">
          <LifeBuoy className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Vous n'avez aucune demande de support en cours.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {tickets.map((t) => {
            const [label, cls] = STATUS[t.status] || ["", ""];
            return (
              <button key={t.id} onClick={() => setOpenId(t.id)} data-testid={`ticket-row-${t.id}`}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-semibold text-slate-400">{t.ticket_number}</span>
                    {t.unread && <span data-testid={`ticket-unread-${t.id}`} className="rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">Non lu</span>}
                  </div>
                  <p className="truncate text-sm font-semibold text-slate-800">{t.subject}</p>
                  <p className="text-xs text-slate-400">{CATS[t.category]} · Mis à jour {timeAgo(t.last_message_at)}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>{label}</span>
              </button>
            );
          })}
        </div>
      )}

      {newOpen && <NewTicketDialog orgId={orgId} onClose={() => setNewOpen(false)} onCreated={() => { qc.invalidateQueries({ queryKey: ["support-tickets"] }); setNewOpen(false); }} />}
      {openId && <TicketDetail orgId={orgId} ticketId={openId} onClose={() => { setOpenId(null); qc.invalidateQueries({ queryKey: ["support-tickets"] }); }} />}
    </div>
  );
}

function NewTicketDialog({ orgId, onClose, onCreated }) {
  const [category, setCategory] = useState(""); const [subject, setSubject] = useState(""); const [message, setMessage] = useState(""); const [file, setFile] = useState(null);
  const mut = useMutation({
    mutationFn: async () => {
      let attachment_id = null;
      if (file) attachment_id = await uploadFile(orgId, file);
      return (await api.post("/api/support/tickets", { category, subject, message, attachment_id }, { params: { organization_id: orgId } })).data;
    },
    onSuccess: (t) => { toast.success(`Demande ${t.ticket_number} créée`); onCreated(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="new-ticket-dialog">
        <DialogHeader><DialogTitle>Nouvelle demande</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <FieldSelect testId="ticket-category-select" className="w-full" value={category} onChange={setCategory} placeholder="Catégorie" options={CAT_OPTS} />
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Objet" data-testid="ticket-subject-input"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none" />
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} placeholder="Décrivez votre demande…" data-testid="ticket-message-input"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none" />
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-500">
            <Paperclip className="h-4 w-4" /> {file ? file.name : "Ajouter une pièce jointe (image ou PDF, max 10 Mo)"}
            <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="ticket-attachment-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Annuler</button>
          <button onClick={() => mut.mutate()} disabled={!category || !subject.trim() || !message.trim() || mut.isPending} data-testid="ticket-submit"
            className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Envoyer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TicketDetail({ orgId, ticketId, onClose }) {
  const qc = useQueryClient();
  const [reply, setReply] = useState(""); const [file, setFile] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["support-ticket", ticketId],
    queryFn: async () => (await api.get(`/api/support/tickets/${ticketId}`, { params: { organization_id: orgId } })).data,
    retry: false,
  });
  const mut = useMutation({
    mutationFn: async () => {
      let attachment_id = null;
      if (file) attachment_id = await uploadFile(orgId, file);
      return (await api.post(`/api/support/tickets/${ticketId}/messages`, { message: reply, attachment_id }, { params: { organization_id: orgId } })).data;
    },
    onSuccess: () => { setReply(""); setFile(null); qc.invalidateQueries({ queryKey: ["support-ticket", ticketId] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  const t = data?.ticket; const msgs = data?.messages || [];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-lg" data-testid="ticket-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono text-xs text-slate-400">{t?.ticket_number}</span>
            <span className="truncate">{t?.subject}</span>
          </DialogTitle>
        </DialogHeader>
        {isLoading ? <div className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" /></div> : (
          <>
            <div className="flex-1 space-y-3 overflow-y-auto py-2" data-testid="ticket-conversation">
              {msgs.map((m) => (
                <div key={m.id} className={`flex ${m.author_type === "client" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.author_type === "client" ? "bg-primary text-white" : "bg-slate-100 text-slate-800"}`}>
                    <p className="mb-0.5 text-[11px] font-semibold opacity-70">{m.author_name}</p>
                    <p className="whitespace-pre-wrap">{m.message}</p>
                    {m.attachment && <a href={`${api.defaults.baseURL}${m.attachment.url}?organization_id=${orgId}`} target="_blank" rel="noopener noreferrer" className="mt-1 flex items-center gap-1 text-xs underline"><Paperclip className="h-3 w-3" />{m.attachment.filename}</a>}
                    <p className="mt-1 text-[10px] opacity-60">{new Date(m.created_at).toLocaleString("fr-FR")}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-end gap-2 border-t border-slate-100 pt-3">
              <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2} placeholder="Votre réponse…" data-testid="ticket-reply-input"
                className="flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none" />
              <label className="cursor-pointer p-2 text-slate-400 hover:text-slate-600"><Paperclip className="h-4 w-4" />
                <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="ticket-reply-attachment" onChange={(e) => setFile(e.target.files?.[0] || null)} /></label>
              <button onClick={() => mut.mutate()} disabled={!reply.trim() || mut.isPending} data-testid="ticket-reply-submit"
                className="flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            {file && <p className="text-xs text-slate-400">{file.name}</p>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
